import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../utils/api'
import './TagSelectionModal.css'

const TagSelectionModal = ({ isOpen, onClose, initialTagType = 'bike' }) => {
    const [step, setStep] = useState(1) // 1: Select Type, 2: Address, 3: Success
    const [tagType, setTagType] = useState('bike') // bike or car
    const [selectedOption, setSelectedOption] = useState(null)
    const [shippingDetails, setShippingDetails] = useState({
        fullName: '',
        mobile: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        landmark: ''
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const { user, isAuthenticated } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (isOpen) {
            // Reset state when modal opens
            setStep(1)
            setTagType(initialTagType)
            setError('')
            setLoading(false)
            if (user) {
                setShippingDetails({
                    fullName: user.name || '',
                    mobile: user.mobile || '',
                    email: user.email || '',
                    addressLine1: '',
                    addressLine2: '',
                    city: '',
                    state: '',
                    pincode: '',
                    landmark: ''
                })
            } else {
                setShippingDetails({
                    fullName: '',
                    mobile: '',
                    email: '',
                    addressLine1: '',
                    addressLine2: '',
                    city: '',
                    state: '',
                    pincode: '',
                    landmark: ''
                })
            }
        }
    }, [isOpen, user, initialTagType])

    const pricingOptions = {
        bike: [
            { id: 'standard', label: 'Standard Tag', price: 499, details: 'Without Accidental Insurance' },
            { id: '1l_ins', label: 'Insurance Plus (1L)', price: 599, details: 'Tag + ₹1 Lakh Accidental Insurance' },
            { id: '2l_ins', label: 'Insurance Pro (2L)', price: 699, details: 'Tag + ₹2 Lakh Accidental Insurance' }
        ],
        car: [
            { id: 'standard', label: 'Standard Tag', price: 599, details: 'Without Accidental Insurance' },
            { id: '3l_ins', label: 'Insurance Pro (3L)', price: 799, details: 'Tag + ₹3 Lakh Accidental Insurance' }
        ]
    }

    const currentPricing = selectedOption || pricingOptions[tagType][0]

    const handleNext = () => {
        if (step === 1) {
            if (!isAuthenticated) {
                onClose()
                navigate('/login')
                return
            }
            if (!selectedOption) {
                setSelectedOption(pricingOptions[tagType][0])
            }
            setStep(2)
        } else if (step === 2) {
            handlePayment()
        }
    }

    useEffect(() => {
        if (tagType && !selectedOption) {
            setSelectedOption(pricingOptions[tagType][0])
        }
    }, [tagType])

    const handleShippingChange = (field, value) => {
        setShippingDetails(prev => ({
            ...prev,
            [field]: value
        }))
        setError('')
    }

    const validateShippingDetails = () => {
        const { fullName, mobile, email, addressLine1, city, state, pincode } = shippingDetails
        
        if (!fullName.trim()) {
            setError('Full name is required')
            return false
        }
        if (!mobile.trim()) {
            setError('Mobile number is required')
            return false
        }
        if (mobile.trim().length < 10) {
            setError('Please enter a valid 10-digit mobile number')
            return false
        }
        const emailTrim = email.trim()
        if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
            setError('Please enter a valid email address')
            return false
        }
        if (!addressLine1.trim()) {
            setError('Address line 1 is required')
            return false
        }
        if (!city.trim()) {
            setError('City is required')
            return false
        }
        if (!state.trim()) {
            setError('State is required')
            return false
        }
        if (!pincode.trim()) {
            setError('Pincode is required')
            return false
        }
        if (pincode.trim().length !== 6 || !/^\d{6}$/.test(pincode.trim())) {
            setError('Please enter a valid 6-digit pincode')
            return false
        }
        return true
    }

    const submitPayuForm = (paymentUrl, params) => {
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = paymentUrl
        form.style.display = 'none'

        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null) return

            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = key
            input.value = String(value)
            form.appendChild(input)
        })

        document.body.appendChild(form)
        form.submit()
    }

    const handlePayment = async () => {
        if (!validateShippingDetails()) {
            return
        }

        setLoading(true)
        setError('')

        try {
            const response = await api.post('/orders/payment/create', {
                amount: currentPricing.price,
                orderData: {
                    tag_type: `${tagType === 'bike' ? 'Bike' : 'Car'} - ${currentPricing.label}`,
                    amount: currentPricing.price,
                    ...(shippingDetails.email.trim() ? { email: shippingDetails.email.trim() } : {}),
                    shipping_address: {
                        full_name: shippingDetails.fullName,
                        mobile: shippingDetails.mobile,
                        address_line1: shippingDetails.addressLine1,
                        address_line2: shippingDetails.addressLine2,
                        city: shippingDetails.city,
                        state: shippingDetails.state,
                        pincode: shippingDetails.pincode,
                        landmark: shippingDetails.landmark
                    },
                    contact_number: shippingDetails.mobile
                }
            })

            const paymentData = response.data

            if (!paymentData.success || !paymentData.paymentUrl || !paymentData.params) {
                throw new Error(paymentData.message || 'Invalid PayU payment response from server')
            }

            submitPayuForm(paymentData.paymentUrl, paymentData.params)
        } catch (err) {
            console.error('Payment initiation error:', err)
            console.error('Error details:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status
            })
            setError(err.response?.data?.message || err.response?.data?.error?.description || err.message || 'Failed to start payment. Please try again.')
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="modal-overlay">
            <div className="modal-content glass-card">
                <button className="modal-close" onClick={onClose}>×</button>

                {step === 1 && (
                    <div className="step-content animate-fadeIn">
                        <h2>Select Your Tag</h2>
                        <p className="step-subtitle">Choose a tag type for your vehicle</p>

                        <div className="tag-options">
                            <div
                                className={`tag-option ${tagType === 'bike' ? 'selected' : ''}`}
                                onClick={() => {
                                    setTagType('bike')
                                    setSelectedOption(pricingOptions.bike[0])
                                }}
                            >
                                <div className="tag-icon">🏍️</div>
                                <h3>Bike Tag</h3>
                                <p>Compact QR for motorcycles</p>
                            </div>

                            <div
                                className={`tag-option ${tagType === 'car' ? 'selected' : ''}`}
                                onClick={() => {
                                    setTagType('car')
                                    setSelectedOption(pricingOptions.car[0])
                                }}
                            >
                                <div className="tag-icon">🚗</div>
                                <h3>Car Tag</h3>
                                <p>Standard QR for cars</p>
                            </div>
                        </div>

                        <div className="insurance-options mt-6">
                            <h4>Select Insurance Plan</h4>
                            <div className="options-grid">
                                {pricingOptions[tagType].map(option => (
                                    <div
                                        key={option.id}
                                        className={`insurance-pill ${selectedOption?.id === option.id ? 'active' : ''}`}
                                        onClick={() => setSelectedOption(option)}
                                    >
                                        <div className="pill-header">
                                            <span className="pill-label">{option.label}</span>
                                            <span className="pill-price">₹{option.price}</span>
                                        </div>
                                        <div className="pill-details">{option.details}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="btn btn-primary full-width mt-4" onClick={handleNext}>
                            Continue
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div className="step-content animate-fadeIn">
                        <h2>Shipping Details</h2>
                        <p className="step-subtitle">Where should we send your tag?</p>

                        <div className="order-summary mb-4">
                            <div className="summary-row">
                                <span>Item:</span>
                                <strong>NAH44 {tagType === 'bike' ? 'Bike' : 'Car'} Tag ({currentPricing.label})</strong>
                            </div>
                            <div className="summary-row">
                                <span>Total:</span>
                                <strong>₹{currentPricing.price.toFixed(2)}</strong>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label>Full Name <span className="required">*</span></label>
                            <input
                                type="text"
                                value={shippingDetails.fullName}
                                onChange={(e) => handleShippingChange('fullName', e.target.value)}
                                placeholder="Enter your full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Mobile Number <span className="required">*</span></label>
                            <input
                                type="tel"
                                value={shippingDetails.mobile}
                                onChange={(e) => handleShippingChange('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                placeholder="10-digit mobile number"
                                maxLength="10"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Email <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
                            <input
                                type="email"
                                value={shippingDetails.email}
                                onChange={(e) => handleShippingChange('email', e.target.value)}
                                placeholder="Receipts — leave blank if you prefer"
                                autoComplete="email"
                            />
                        </div>

                        <div className="form-group">
                            <label>Address Line 1 <span className="required">*</span></label>
                            <input
                                type="text"
                                value={shippingDetails.addressLine1}
                                onChange={(e) => handleShippingChange('addressLine1', e.target.value)}
                                placeholder="House/Flat No., Building Name, Street"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Address Line 2</label>
                            <input
                                type="text"
                                value={shippingDetails.addressLine2}
                                onChange={(e) => handleShippingChange('addressLine2', e.target.value)}
                                placeholder="Area, Locality (Optional)"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>City <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={shippingDetails.city}
                                    onChange={(e) => handleShippingChange('city', e.target.value)}
                                    placeholder="City"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>State <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={shippingDetails.state}
                                    onChange={(e) => handleShippingChange('state', e.target.value)}
                                    placeholder="State"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Pincode <span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={shippingDetails.pincode}
                                    onChange={(e) => handleShippingChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="6-digit pincode"
                                    maxLength="6"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Landmark</label>
                                <input
                                    type="text"
                                    value={shippingDetails.landmark}
                                    onChange={(e) => handleShippingChange('landmark', e.target.value)}
                                    placeholder="Near (Optional)"
                                />
                            </div>
                        </div>

                            <button
                                className="btn btn-primary full-width mt-4"
                                onClick={handlePayment}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : `Pay ₹${currentPricing.price} & Order`}
                            </button>
                        <button
                            className="btn btn-text full-width mt-2"
                            onClick={() => setStep(1)}
                            disabled={loading}
                        >
                            Back
                        </button>
                    </div>
                )}

                {step === 3 && (
                    <div className="step-content text-center animate-fadeIn">
                        <div className="success-icon">✅</div>
                        <h2>Order Placed!</h2>
                        <p>Your order has been successfully placed. We will ship it soon.</p>

                        <div className="action-buttons mt-6">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    onClose()
                                    navigate('/orders')
                                }}
                            >
                                View My Orders
                            </button>
                            <button className="btn btn-secondary ml-2" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TagSelectionModal
